import { createReducer, on } from '@ngrx/store';

import {
  addShouldRecalculate,
  cropYearChanged,
  loadBasicSpraysSuccess,
  loadCustomerBlightRights,
  loadCustomerBlightRightsFailure,
  loadCustomerBlightRightsSuccess,
  loadLegalRestrictionsSuccess,
  loadSprayApplicationsSuccess,
  removeSelectedBlightCropField,
  removeShouldRecalculate,
  setBlightAdviceDate,
  setBlightAdviceDateOneDayBack,
  setBlightAdviceDateOneDayForward,
  setBlightChartDetails,
  setBlightRecalculationStatusPerCropField,
  setCropFieldRegistration,
  setCropFieldsForSpray,
  setIsSpraysLoading,
  setNewBlightDataForCropField,
  setSelectedBlightCropFieldIds,
  setSelectedCropField,
  setShouldGetNewBlightData,
  setShowRecalculationFinishedNotification,
  updateCropFieldVisibilityStatusSuccess,
  updateEmergenceDate,
  updateHarvestDate,
  updatePlantDate,
  updateSuppressSprayAdvice,
} from './actions';
import {
  loadBlightData,
  loadBlightDataSuccess,
  loadParticipatingCropFieldsSuccess,
  resetBlightData,
} from '../blight-v2/store/actions';
import { AppState, BlightRecalculationStatusPerCropField } from '../shared/models';

export const initialAppState: AppState = {
  blightData: [],
  blightParticipatingCropFields: [],
  blightChartDetails: null,
  blightRecalculationStatusPerCropField: [],
  downyMildewData: [],
  downyMildewRecalculationStatusPerCropField: [],
  showRecalculationFinishedNotification: false,
  customerBlightRights: null,
  basicSprays: [],
  blightAdviceDate: new Date(),
  sprayApplications: undefined,
  isSpraysLoading: false,
  newBlightDataForCropField: {
    cropFieldId: -1,
    timestamp: new Date(),
  },
  shouldRecalculateForCropFieldIds: [],
  shouldGetNewBlightData: false,
  cropFieldRegistration: null,
  selectedCropField: null,
  selectedBlightCropFieldIds: [],
  cropFieldsIdsForSpray: undefined,
  legalRestrictions: [],
};

export const appReducer = createReducer(
  initialAppState,

  on(loadCustomerBlightRights, loadCustomerBlightRightsFailure, (state): AppState => ({
    ...state,
    customerBlightRights: null,
  })),

  on(setIsSpraysLoading, (state, { isSpraysLoading }) => ({
    ...state,
    isSpraysLoading,
  })),

  on(loadBasicSpraysSuccess, (state, { payload: { basicSprays } }) => ({
    ...state,
    basicSprays,
  })),

  on(loadSprayApplicationsSuccess, (state, { payload: { sprayApplications } }) => ({
    ...state,
    sprayApplications,
  })),

  on(setBlightChartDetails, (state, { blightChartDetails }) => ({
    ...state,
    blightChartDetails,
  })),

  on(loadCustomerBlightRightsSuccess, (state, { payload: { customerBlightRights } }) => ({
    ...state,
    customerBlightRights,
  })),

  on(setSelectedBlightCropFieldIds, (state, { cropFieldIds }) => ({
    ...state,
    selectedBlightCropFieldIds: cropFieldIds,
  })),

  on(removeSelectedBlightCropField, (state, { cropFieldId }) => ({
    ...state,
    selectedBlightCropFieldIds: state.selectedBlightCropFieldIds.filter((x) => x !== cropFieldId),
  })),

  on(setNewBlightDataForCropField, (state, { cropFieldId, timestamp }) => ({
    ...state,
    newBlightDataForCropField: {
      cropFieldId,
      timestamp,
    },
  })),

  on(addShouldRecalculate, (state, { cropFieldId }) => ({
    ...state,
    shouldRecalculateForCropFieldIds: [...state.shouldRecalculateForCropFieldIds, cropFieldId],
  })),

  on(removeShouldRecalculate, (state, { cropFieldId }) => ({
    ...state,
    shouldRecalculateForCropFieldIds: state.shouldRecalculateForCropFieldIds.filter((x) => x !== cropFieldId),
  })),

  on(setCropFieldRegistration, (state, { cropFieldRegistration }) => ({
    ...state,
    cropFieldRegistration,
  })),

  on(updatePlantDate, (state, { date }) => ({
    ...state,
    cropFieldRegistration: state.cropFieldRegistration
      ? { ...state.cropFieldRegistration, plantDate: date }
      : null,
  })),

  on(updateEmergenceDate, (state, { date }) => ({
    ...state,
    cropFieldRegistration: state.cropFieldRegistration
      ? { ...state.cropFieldRegistration, emergenceDate: date, id: state.cropFieldRegistration.id }
      : null,
  })),

  on(updateHarvestDate, (state, { date }) => ({
    ...state,
    cropFieldRegistration: state.cropFieldRegistration
      ? { ...state.cropFieldRegistration, harvestDate: date, id: state.cropFieldRegistration.id }
      : null,
  })),

  on(updateSuppressSprayAdvice, (state, { suppressSprayAdvice }) => ({
    ...state,
    cropFieldRegistration: state.cropFieldRegistration
      ? { ...state.cropFieldRegistration, suppressSprayAdvice, id: state.cropFieldRegistration.id }
      : null,
  })),

  on(setShowRecalculationFinishedNotification, (state, { value }) => ({
    ...state,
    showRecalculationFinishedNotification: value,
  })),

  on(setShouldGetNewBlightData, (state, { shouldGetNewBlightData }) => ({
    ...state,
    shouldGetNewBlightData,
  })),

  on(setBlightRecalculationStatusPerCropField, (state, { blightRecalculationStatusPerCropField }) => {
    let updatedRecalculationStatuses: BlightRecalculationStatusPerCropField[];
    let showRecalculationFinishedNotification: boolean;

    if (state.blightRecalculationStatusPerCropField.length > 0) {
      const existingCropField = state.blightRecalculationStatusPerCropField.find((x) =>
        x.cropFieldId === blightRecalculationStatusPerCropField.cropFieldId,
      );

      if (existingCropField) {
        existingCropField.isBlightRecalculating = blightRecalculationStatusPerCropField.isBlightRecalculating;
        updatedRecalculationStatuses = state.blightRecalculationStatusPerCropField;
      } else {
        updatedRecalculationStatuses = [...state.blightRecalculationStatusPerCropField, blightRecalculationStatusPerCropField];
      }

      showRecalculationFinishedNotification = !state.blightRecalculationStatusPerCropField.some((status) => status.isBlightRecalculating);
    } else {
      updatedRecalculationStatuses = [blightRecalculationStatusPerCropField];
      showRecalculationFinishedNotification = false;
    }

    return {
      ...state,
      blightRecalculationStatusPerCropField: updatedRecalculationStatuses,
      showRecalculationFinishedNotification,
    };
  }),

  on(setSelectedCropField, (state, { cropField }) => ({
    ...state,
    selectedCropField: cropField,
  })),

  on(loadLegalRestrictionsSuccess, (state, { payload: { legalRestrictions } }) => ({
    ...state,
    legalRestrictions,
  })),

  on(setCropFieldsForSpray, (state, { cropFieldIds }) => ({
    ...state,
    cropFieldsIdsForSpray: cropFieldIds,
  })),

  on(setBlightAdviceDate, (state, { adviceDate }) => ({
    ...state,
    blightAdviceDate: adviceDate,
  })),

  on(setBlightAdviceDateOneDayBack, (state) => {
    const previousDay = new Date(state.blightAdviceDate);
    previousDay.setDate(state.blightAdviceDate.getDate() - 1);

    return {
      ...state,
      blightAdviceDate: previousDay,
    };
  }),

  on(setBlightAdviceDateOneDayForward, (state) => {
    const nextDay = new Date(state.blightAdviceDate);
    nextDay.setDate(state.blightAdviceDate.getDate() + 1);

    return {
      ...state,
      blightAdviceDate: nextDay,
    };
  }),

  on(updateCropFieldVisibilityStatusSuccess, (state, action): AppState => ({
    ...state,
    blightData: state.blightData.map((bd) =>
      bd.cropFieldId === action.cropFieldId
        ? { ...bd, isCropFieldVisible: action.isVisible }
        : bd,
    ),
  })),

  on(loadBlightData, (state) => ({
    ...state,
  })),

  on(loadBlightDataSuccess, (state, { payload: { data } }): AppState => ({
    ...state,
    blightData: data,
  })),

  on(resetBlightData, (state): AppState => ({
    ...state,
    blightData: [],
  })),

  on(cropYearChanged, (state): AppState => ({
    ...state,
    customerBlightRights: null,
    blightData: [],
    blightParticipatingCropFields: [],
    blightChartDetails: null,
    blightRecalculationStatusPerCropField: [],
    downyMildewData: [],
    downyMildewRecalculationStatusPerCropField: [],
    showRecalculationFinishedNotification: false,
    basicSprays: state.basicSprays,
    blightAdviceDate: new Date(),
    sprayApplications: undefined,
    isSpraysLoading: false,
    newBlightDataForCropField: initialAppState.newBlightDataForCropField,
    shouldRecalculateForCropFieldIds: [],
    shouldGetNewBlightData: false,
    cropFieldRegistration: null,
    selectedCropField: null,
    selectedBlightCropFieldIds: [],
    cropFieldsIdsForSpray: undefined,
    legalRestrictions: state.legalRestrictions,
  })),

  on(loadParticipatingCropFieldsSuccess, (state, { payload: { cropFields } }): AppState => ({
    ...state,
    blightParticipatingCropFields: cropFields,
  })),
);
